import { apiClient } from './client';
import type { Folder, FolderCollaborator, FolderInvitation, RecipeListItem } from '@/types';

interface FoldersResponse {
  folders: Folder[];
}

interface FolderResponse {
  folder: Folder;
}

interface FolderRecipesResponse {
  recipes: RecipeListItem[];
}

interface CollaboratorsResponse {
  collaborators: FolderCollaborator[];
  owner: {
    user_id: string;
    username: string;
    email: string;
  };
}

interface InvitationsResponse {
  invitations: FolderInvitation[];
}

// Folder CRUD
export async function getFolders(): Promise<Folder[]> {
  const data = await apiClient.get<FoldersResponse>('/folders');
  return data.folders;
}

export async function createFolder(name: string): Promise<Folder> {
  const data = await apiClient.post<FolderResponse>('/folders', { name });
  return data.folder;
}

export async function updateFolder(id: string, name: string): Promise<Folder> {
  const data = await apiClient.put<FolderResponse>(`/folders/${id}`, { name });
  return data.folder;
}

export async function deleteFolder(id: string): Promise<void> {
  await apiClient.delete(`/folders/${id}`);
}

// Folder recipes
export async function getFolderRecipes(folderId: string): Promise<RecipeListItem[]> {
  const data = await apiClient.get<FolderRecipesResponse>(`/folders/${folderId}/recipes`);
  return data.recipes;
}

export async function addRecipeToFolder(folderId: string, recipeId: string): Promise<void> {
  await apiClient.post(`/folders/${folderId}/recipes`, { recipeId });
}

export async function removeRecipeFromFolder(folderId: string, recipeId: string): Promise<void> {
  await apiClient.delete(`/folders/${folderId}/recipes/${recipeId}`);
}

export async function getRecipeFolders(recipeId: string): Promise<string[]> {
  const data = await apiClient.get<{ folderIds: string[] }>(`/recipes/${recipeId}/folders`);
  return data.folderIds || [];
}

export async function updateRecipeFolders(recipeId: string, folderIds: string[]): Promise<void> {
  await apiClient.put(`/recipes/${recipeId}/folders`, { folder_ids: folderIds });
}

// Collaboration
export async function getCollaborators(folderId: string): Promise<CollaboratorsResponse> {
  return apiClient.get<CollaboratorsResponse>(`/folders/${folderId}/collaborators`);
}

export async function inviteCollaborator(folderId: string, email: string): Promise<void> {
  await apiClient.post(`/folders/${folderId}/invite`, { email });
}

export async function removeCollaborator(folderId: string, userId: string): Promise<void> {
  await apiClient.delete(`/folders/${folderId}/collaborators/${userId}`);
}

export async function getPendingInvitations(): Promise<FolderInvitation[]> {
  const data = await apiClient.get<InvitationsResponse>('/invitations/pending');
  return data.invitations;
}

export async function acceptInvitation(token: string): Promise<void> {
  await apiClient.post(`/invitations/${token}/accept`);
}

export async function declineInvitation(token: string): Promise<void> {
  await apiClient.post(`/invitations/${token}/decline`);
}
